import { gql, type TypedDocumentNode } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Auction } from '@sorare/core/src/atoms/icons/Auction';
import { InstantBuy } from '@sorare/core/src/atoms/icons/InstantBuy';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BodyM,
  BodyS,
  LabelL,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { Ordinal } from '@sorare/core/src/atoms/typography/Ordinal';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { Dialog } from '@sorare/core/src/components/dialog';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { buildFilterQuery } from '@sorare/core/src/components/search/InstantSearch/utils';
import { ANY_SPORT_MARKET_SHOP_INSTANT_BUY } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { glossary, wheel } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { scarcityMessages } from '@sorare/core/src/lib/scarcity';
import { ALL_WHEEL_TICKETS } from '@sorare/core/src/lib/wheelCurrencies';

import type {
  HowToWinTicketDialogQuery,
  HowToWinTicketDialogQueryVariables,
} from './__generated__/index.graphql.ts';

const Content = styled(Vertical).attrs({
  gap: 5,
})`
  padding: var(--triple-unit);
`;

const Bullet = styled(Horizontal).attrs({
  gap: 2,
})`
  > :first-child {
    flex-shrink: 0;
  }
`;

const EligibilitySection = styled(Vertical).attrs({
  gap: 1.5,
})`
  border-top: 1px solid var(--c-nd-200);
  padding-top: var(--double-unit);
`;

const PillList = styled(Horizontal)`
  flex-wrap: wrap;
  gap: var(--half-unit);
`;

const LogoPill = styled(Horizontal).attrs({
  gap: 0.5,
})`
  background: var(--c-nd-50);
  border-radius: 16px;
  padding: 2px var(--unit) 2px 2px;
  align-items: center;
`;

const PillLogo = styled.img`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  object-fit: cover;
`;

const TICKETS_CURRENCY = ALL_WHEEL_TICKETS;

const QUERY = gql`
  query HowToWinTicketDialogQuery($rarity: Rarity!, $sport: Sport!) {
    market {
      id
      activeWheelConfig(rarity: $rarity, sport: $sport) {
        slug
        algoliaFilters
        ticketsRewardedAfterPurchase {
          id
          amount
          currency
        }
        bidToEarnRewardConfigs {
          bidderRank
          rewards {
            id
            ... on InGameCurrencyRewardConfig {
              id
              amount
              currency
            }
          }
        }
        rewardPurchaseEligibilityRules {
          id
          eligibleRarities
          eligibleCardEditions
          eligibleCompetitions {
            slug
            displayName
            pictureUrl(derivative: "avatar")
          }
          eligibleTeams {
            ... on TeamInterface {
              slug
              name
              pictureUrl(derivative: "avatar")
            }
          }
        }
      }
    }
  }
` as TypedDocumentNode<
  HowToWinTicketDialogQuery,
  HowToWinTicketDialogQueryVariables
>;

type Props = {
  open: boolean;
  rarity?: Rarity;
  onClose: () => void;
};

export const HowToWinTicketDialog = ({
  rarity = Rarity.limited,
  open,
  onClose,
}: Props) => {
  const { sport, generateSportContextPath } = useSportContext();
  const { formatMessage } = useIntl();

  const { data } = useQuery(QUERY, {
    variables: {
      rarity,
      sport,
    },
    skip: !open,
  });

  const wheelConfig = data?.market?.activeWheelConfig;
  if (!wheelConfig) {
    return null;
  }
  const {
    ticketsRewardedAfterPurchase,
    bidToEarnRewardConfigs,
    rewardPurchaseEligibilityRules,
  } = wheelConfig;

  const nbTicketsAfterPurchase = TICKETS_CURRENCY.includesValue(
    ticketsRewardedAfterPurchase?.currency
  )
    ? ticketsRewardedAfterPurchase?.amount
    : 0;

  const ticketsForRank = bidToEarnRewardConfigs.reduce<
    { rank: number; nbTickets: number }[]
  >((acc, bidToEarn) => {
    const inGameCurrencyRewards = bidToEarn.rewards
      .filter(reward => isType(reward, 'InGameCurrencyRewardConfig'))
      .filter(reward => TICKETS_CURRENCY.includesValue(reward.currency));

    if (inGameCurrencyRewards.length > 0) {
      acc.push({
        rank: bidToEarn.bidderRank,
        nbTickets: inGameCurrencyRewards[0].amount,
      });
    }

    return acc;
  }, []);

  const rawRarities = rewardPurchaseEligibilityRules?.eligibleRarities;
  const eligibleCardEditions =
    rewardPurchaseEligibilityRules?.eligibleCardEditions;
  const eligibleCompetitions =
    rewardPurchaseEligibilityRules?.eligibleCompetitions;
  const eligibleTeams = rewardPurchaseEligibilityRules?.eligibleTeams;

  const hasEligibilityCriteria =
    (rawRarities && rawRarities.length > 0) ||
    (eligibleCardEditions && eligibleCardEditions.length > 0) ||
    (eligibleCompetitions && eligibleCompetitions.length > 0) ||
    (eligibleTeams && eligibleTeams.length > 0);

  const { algoliaFilters } = wheelConfig;
  const basePath = generateSportContextPath(ANY_SPORT_MARKET_SHOP_INSTANT_BUY);
  const query = algoliaFilters
    ? buildFilterQuery({ [SEARCH_PARAMS.ALGOLIA_FILTERS]: algoliaFilters })
    : '';
  const marketUrl = query ? `${basePath}?${query}` : basePath;

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      onClose={onClose}
      title={
        <LabelL bold>
          <FormattedMessage {...wheel.howToWin} />
        </LabelL>
      }
    >
      <Content>
        <Vertical gap={2}>
          {nbTicketsAfterPurchase > 0 && (
            <>
              <Bullet>
                <InstantBuy size={3} color="var(--c-white)" />
                <BodyM color="var(--c-nd-600)" as="p">
                  <FormattedMessage
                    defaultMessage="Buy an eligible Card through Instant Buy to win {nbTickets, plural, one {a ticket} other {# tickets}}"
                    description="v3"
                    values={{ nbTickets: nbTicketsAfterPurchase }}
                  />
                </BodyM>
              </Bullet>
              <Bullet>
                <Auction size={3} color="var(--c-white)" />
                <BodyM color="var(--c-nd-600)" as="p">
                  <FormattedMessage
                    defaultMessage="Buy an eligible Card through Auction to win {nbTickets, plural, one {a ticket} other {# tickets}}"
                    description="v3"
                    values={{ nbTickets: nbTicketsAfterPurchase }}
                  />
                </BodyM>
              </Bullet>
            </>
          )}
          {ticketsForRank.map(({ rank, nbTickets }) => (
            <Bullet key={rank}>
              <Auction size={3} color="var(--c-white)" />
              <BodyM color="var(--c-nd-600)" as="p">
                <FormattedMessage
                  defaultMessage="End an eligible Card Auction with the {rank} highest bid to win {nbTickets, plural, one {a ticket} other {# tickets}}"
                  description="v3"
                  values={{
                    rank: <Ordinal ordinal={rank + 1} />,
                    nbTickets,
                  }}
                />
              </BodyM>
            </Bullet>
          ))}
        </Vertical>
        {!!hasEligibilityCriteria && (
          <EligibilitySection>
            <BodyS bold color="var(--c-nd-400)">
              <FormattedMessage defaultMessage="Eligible cards" />
            </BodyS>
            {!!(rawRarities && rawRarities.length > 0) && (
              <PillList>
                {rawRarities.map(r => (
                  <Chip key={r} size="small">
                    <ScarcityIcon rarity={r} size={1.5} />
                    <LabelS>
                      {formatMessage(
                        scarcityMessages[r as keyof typeof scarcityMessages] ??
                          scarcityMessages.limited
                      )}
                    </LabelS>
                  </Chip>
                ))}
              </PillList>
            )}
            {!!(eligibleCardEditions && eligibleCardEditions.length > 0) && (
              <PillList>
                {eligibleCardEditions.map(edition => (
                  <Chip key={edition} size="small">
                    <LabelS>{edition}</LabelS>
                  </Chip>
                ))}
              </PillList>
            )}
            {!!(eligibleCompetitions && eligibleCompetitions.length > 0) && (
              <PillList>
                {eligibleCompetitions.map(c => (
                  <LogoPill key={c.slug}>
                    {!!c.pictureUrl && (
                      <PillLogo src={c.pictureUrl} alt={c.displayName} />
                    )}
                    <LabelS>{c.displayName}</LabelS>
                  </LogoPill>
                ))}
              </PillList>
            )}
            {!!(eligibleTeams && eligibleTeams.length > 0) && (
              <PillList>
                {eligibleTeams.map(t => (
                  <LogoPill key={t.slug}>
                    {!!t.pictureUrl && (
                      <PillLogo src={t.pictureUrl} alt={t.name} />
                    )}
                    <LabelS>{t.name}</LabelS>
                  </LogoPill>
                ))}
              </PillList>
            )}
          </EligibilitySection>
        )}
        <Vertical>
          <Button size="large" fullWidth color="primary" to={marketUrl}>
            <FormattedMessage defaultMessage="Explore the market" />
          </Button>
          <Button size="large" fullWidth color="quaternary" onClick={onClose}>
            <FormattedMessage {...glossary.close} />
          </Button>
        </Vertical>
      </Content>
    </Dialog>
  );
};
